import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { teamAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Users, Check, X, Globe, MapPin, Shield, LogIn } from 'lucide-react';
import { toast } from 'sonner';

const AcceptInvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [inviteData, setInviteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    validateInvite();
  }, [token]);

  const validateInvite = async () => {
    try {
      const res = await teamAPI.validateInvite(token);
      setInviteData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired invitation link.');
    } finally { setLoading(false); }
  };

  const handleAccept = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in first to accept this invitation.');
      navigate(`/login?redirect=/invite/${token}`);
      return;
    }
    setAccepting(true);
    try {
      const res = await teamAPI.acceptInvite(token);
      toast.success(res.data.message);
      navigate('/dashboard/team');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept invitation');
    } finally { setAccepting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center" data-testid="invite-error">
        <X className="w-12 h-12 text-[#D93025] mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-[#202124] mb-2" style={{ fontFamily: 'Outfit' }}>Invitation Invalid</h2>
        <p className="text-sm text-[#5F6368] mb-6">{error}</p>
        <Link to="/" className="btn-google inline-flex items-center gap-2">Go to Home</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full" data-testid="invite-accept-card">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-[#1A73E8] to-[#0D47A1] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Team Invitation</h2>
          <p className="text-sm text-[#5F6368] mt-1">You've been invited to join a team</p>
        </div>

        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-3 p-3 bg-[#F8F9FA] rounded-lg">
            <Shield className="w-5 h-5 text-[#1A73E8] flex-shrink-0" />
            <div>
              <p className="text-xs text-[#5F6368]">Invited by</p>
              <p className="text-sm font-medium text-[#202124]">{inviteData?.owner_name || inviteData?.owner_email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-[#F8F9FA] rounded-lg">
            <Users className="w-5 h-5 text-[#1A73E8] flex-shrink-0" />
            <div>
              <p className="text-xs text-[#5F6368]">Role</p>
              <p className="text-sm font-medium text-[#202124] capitalize">{inviteData?.role}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-[#F8F9FA] rounded-lg">
            {inviteData?.access_type === 'global' ? (
              <Globe className="w-5 h-5 text-[#1A73E8] flex-shrink-0" />
            ) : (
              <MapPin className="w-5 h-5 text-[#1A73E8] flex-shrink-0" />
            )}
            <div>
              <p className="text-xs text-[#5F6368]">Access</p>
              <p className="text-sm font-medium text-[#202124]">
                {inviteData?.access_type === 'global' ? 'All Locations' : `${inviteData?.location_count || 0} specific location(s)`}
              </p>
            </div>
          </div>
        </div>

        {isAuthenticated ? (
          <div className="space-y-3">
            <p className="text-xs text-center text-[#5F6368]">Logged in as <strong>{user?.email}</strong></p>
            <button
              onClick={handleAccept}
              disabled={accepting}
              className="w-full btn-google flex items-center justify-center gap-2"
              data-testid="accept-invite-btn"
            >
              {accepting ? (
                <><span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Accepting...</>
              ) : (
                <><Check className="w-4 h-4" /> Accept Invitation</>
              )}
            </button>
            <Link to="/" className="block w-full text-center py-2.5 bg-[#F1F3F4] text-[#5F6368] rounded-lg text-sm font-medium hover:bg-[#E8EAED] transition-colors" data-testid="decline-invite-btn">
              Decline
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-center text-[#5F6368]">Please log in or register to accept this invitation.</p>
            <Link
              to={`/login?redirect=/invite/${token}`}
              className="w-full btn-google flex items-center justify-center gap-2"
              data-testid="login-to-accept-btn"
            >
              <LogIn className="w-4 h-4" /> Log In to Accept
            </Link>
            <Link
              to={`/register?redirect=/invite/${token}`}
              className="block w-full text-center py-2.5 bg-[#E8F0FE] text-[#1A73E8] rounded-lg text-sm font-medium hover:bg-[#D2E3FC] transition-colors"
              data-testid="register-to-accept-btn"
            >
              Create Account
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceptInvitePage;
